/*
 * Combine Home — Visualizador 3D da MESA DE JANTAR FLORENCE (v1)
 * Botão "Ver em 3D" abaixo do botão de compra → aba lateral (direita → esquerda) com o modelo 3D,
 * escolha de material e tamanho (variantes reais do produto) e "Adicionar ao carrinho".
 * ES module. Requer o importmap do Three.js no <head> (snippets/configurador-importmap.liquid).
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const I18N = {
  pt: { reset: 'Repor vista', hint: 'Arraste para rodar · role para aproximar', loading: 'A preparar o modelo…', ar: 'Ver no meu espaço', close: 'Fechar', adding: 'A adicionar…', added: 'Adicionado', arDesktop: 'Abra esta página no telemóvel para ver a mesa na sua sala.', arFail: 'Não foi possível abrir a realidade aumentada neste dispositivo.', arPlace: 'Aponte para o chão e toque para colocar', arMove: 'Toque noutro sítio para mudar de lugar', exit: 'Sair', unavailable: 'Esta combinação não está disponível de momento.', error: 'Não foi possível adicionar ao carrinho. Tente novamente.' },
  fr: { reset: 'Réinitialiser la vue', hint: 'Faites glisser pour tourner · molette pour zoomer', loading: 'Préparation du modèle…', ar: 'Voir chez moi', close: 'Fermer', adding: 'Ajout…', added: 'Ajouté', arDesktop: 'Ouvrez cette page sur votre téléphone pour voir la table chez vous.', arFail: 'Impossible d’ouvrir la réalité augmentée sur cet appareil.', arPlace: 'Visez le sol et touchez pour placer', arMove: 'Touchez ailleurs pour déplacer', exit: 'Quitter', unavailable: 'Cette combinaison n’est pas disponible pour le moment.', error: 'Impossible d’ajouter au panier. Veuillez réessayer.' },
  en: { reset: 'Reset view', hint: 'Drag to rotate · scroll to zoom', loading: 'Preparing the model…', ar: 'View in my space', close: 'Close', adding: 'Adding…', added: 'Added', arDesktop: 'Open this page on your phone to see the table in your room.', arFail: 'Augmented reality could not be opened on this device.', arPlace: 'Point at the floor and tap to place', arMove: 'Tap elsewhere to move it', exit: 'Exit', unavailable: 'This combination is not available at the moment.', error: 'Could not add to cart. Please try again.' },
};

const boot = () => document.querySelectorAll('[data-fl-root]:not([data-fl-bound])').forEach(bind);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

function bind(root) {
  root.setAttribute('data-fl-bound', '');
  const CFG = JSON.parse(root.querySelector('script[data-fl-config]').textContent);
  const T = I18N[CFG.lang] || I18N.pt;
  const drawer = root.querySelector('[data-fl-drawer]');
  const wrap = root.querySelector('[data-fl-trigger-wrap]');

  // Botão "Ver em 3D" logo a seguir ao botão de compra do tema (Shella)
  const anchor = document.querySelector('.product-page-info__button-add-to-cart')
    || document.querySelector('.product-page-info form[action*="/cart/add"]');
  if (anchor) anchor.insertAdjacentElement('afterend', wrap);
  wrap.hidden = false;
  document.body.appendChild(drawer);   // a aba fica fora de contentores com transform/overflow do tema

  let viewer = null, lastFocus = null;
  const open = () => {
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.documentElement.classList.add('fl-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
    if (!viewer) viewer = createViewer(drawer, CFG, T, close);
    viewer.syncFromPage();
    viewer.setActive(true);
    drawer.querySelector('.fl-drawer__close').focus({ preventScroll: true, focusVisible: false });
  };
  const close = () => {
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('fl-lock');
    if (viewer) viewer.setActive(false);
    const done = () => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; };
    drawer.querySelector('.fl-drawer__panel').addEventListener('transitionend', done, { once: true });
    setTimeout(done, 500);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };
  wrap.querySelector('[data-fl-open]').addEventListener('click', e => { e.preventDefault(); open(); });
  drawer.querySelectorAll('[data-fl-close]').forEach(b => b.addEventListener('click', close));
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  if (location.hash === '#ver-3d' || location.hash === '#3d') open();
}

function createViewer(drawer, CFG, T, closeDrawer) {
  const q = s => drawer.querySelector(`[data-fl-${s}]`);
  q('reset').textContent = T.reset; q('hint').textContent = T.hint; q('loading').textContent = T.loading;
  q('ar').textContent = T.ar; q('closelabel').textContent = T.close;

  /* ---------- Opções do produto (funciona em PT, FR e EN) ---------- */
  const SIZE_RE = /(\d{2,3})\s*[x×]\s*(\d{2,3})\s*[x×]\s*(\d{2,3})/i;
  const opts = CFG.options;                                  // [{name, position, values}]
  const sizeOpt = opts.find(o => o.values.every(v => SIZE_RE.test(v)));
  const matOpt = opts.find(o => o !== sizeOpt);
  const MAT_KEYS = [
    ['nogueira', /nogueira|noyer|walnut|nogal/i],
    ['natural', /natural|naturel/i],
    ['dark', /dark|fonc|escuro|oscuro/i],
    ['preto', /preto|noir|black|negro/i],
  ];
  const matKey = (v, i) => (MAT_KEYS.find(([, re]) => re.test(v)) || MAT_KEYS[i % 4] || MAT_KEYS[0])[0];
  const MATS = matOpt.values.map((v, i) => ({ value: v, key: matKey(v, i) }));
  const BASES = { 220: [85, 50], 250: [97, 54] };            // pé oval (C × L) por comprimento do tampo
  const SIZES = sizeOpt.values.map(v => {
    const [, L, W, H] = v.match(SIZE_RE).map(Number);
    const [bL, bW] = BASES[L] || [Math.round(L * 0.386), Math.round(W * 0.455)];
    return { value: v, L, W, H, baseL: bL, baseW: bW };
  });
  const optVal = (variant, opt) => variant['option' + opt.position];
  const findVariant = (m, s) => CFG.variants.find(v => optVal(v, matOpt) === MATS[m].value && optVal(v, sizeOpt) === SIZES[s].value);
  const state = { mat: 0, size: 0 };
  const selectVariantId = id => {
    const v = CFG.variants.find(x => String(x.id) === String(id)); if (!v) return false;
    state.mat = Math.max(0, MATS.findIndex(m => m.value === optVal(v, matOpt)));
    state.size = Math.max(0, SIZES.findIndex(s => s.value === optVal(v, sizeOpt)));
    return true;
  };
  selectVariantId(CFG.selected);

  const money = cents => {
    const fmt = CFG.moneyFormat || '€{{amount}}';
    let out;
    try { if (window.Shopify && typeof window.Shopify.formatMoney === 'function') out = window.Shopify.formatMoney(cents, fmt); } catch (e) {}
    if (!out) {
      const n = cents / 100, g = (v, d, th, dec) => { const [i, f] = v.toFixed(d).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? dec + f : ''); };
      out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({ amount: g(n, 2, ',', '.'), amount_no_decimals: g(n, 0, ',', '.'), amount_with_comma_separator: g(n, 2, '.', ','), amount_no_decimals_with_comma_separator: g(n, 0, '.', ',') })[k] ?? g(n, 2, ',', '.'));
    }
    return out.replace(/<[^>]*>/g, '');
  };

  /* ---------- UI ---------- */
  q('matname').textContent = matOpt.name; q('sizename').textContent = sizeOpt.name;
  q('mats').innerHTML = MATS.map((m, i) => `<button type="button" data-i="${i}" title="${m.value}" aria-label="${m.value}" aria-pressed="false"><img src="${CFG.thumbs[m.key]}" alt="" width="52" height="52" loading="lazy"></button>`).join('');
  q('sizes').innerHTML = SIZES.map((s, i) => `<button type="button" data-i="${i}" aria-pressed="false">${s.value}</button>`).join('');
  q('mats').addEventListener('click', e => { const b = e.target.closest('[data-i]'); if (b) { state.mat = +b.dataset.i; update(); } });
  q('sizes').addEventListener('click', e => { const b = e.target.closest('[data-i]'); if (b) { state.size = +b.dataset.i; update(); } });
  q('add').addEventListener('click', addToCart);
  q('ar').addEventListener('click', startAR);
  q('reset').addEventListener('click', resetView);

  function renderUI() {
    const v = findVariant(state.mat, state.size), s = SIZES[state.size];
    q('mats').querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-pressed', i === state.mat));
    q('sizes').querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-pressed', i === state.size));
    q('matvalue').textContent = MATS[state.mat].value; q('sizevalue').textContent = s.value;
    q('price').textContent = v ? money(v.price) : '';
    q('compare').textContent = v && v.compare_at_price > v.price ? money(v.compare_at_price) : '';
    q('dims').textContent = `${s.L} × ${s.W} × ${s.H} cm`;
    const ok = v && v.available !== false;
    q('add').disabled = !ok;
    q('msg').textContent = ok ? '' : T.unavailable;
  }

  async function addToCart() {
    const v = findVariant(state.mat, state.size); if (!v) return;
    const btn = q('add'), label = q('addlabel'), idle = label.textContent;
    btn.disabled = true; label.textContent = T.adding; q('msg').textContent = '';
    try {
      const res = await fetch(CFG.cartAddUrl || '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || err.message || ''); }
      document.dispatchEvent(new CustomEvent('fl:cart:added', { detail: { variantId: v.id } }));
      const cart = window.theme && window.theme.Cart;
      if (cart && typeof cart.updateCart === 'function') {
        await cart.updateCart();
        label.textContent = T.added;
        closeDrawer();
        if (typeof cart.showPopup === 'function') setTimeout(() => cart.showPopup(), 380);
        setTimeout(() => { label.textContent = idle; btn.disabled = false; }, 1500);
      } else {
        location.href = CFG.cartUrl || '/cart';
      }
    } catch (e) {
      label.textContent = idle; btn.disabled = false;
      q('msg').textContent = e.message ? `${T.error} (${e.message})` : T.error;
    }
  }

  /* ---------- Cena 3D ---------- */
  const stage = q('stage'), canvas = q('canvas');
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.xr.enabled = true;
  const scene = new THREE.Scene();
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.8;
  scene.add(new THREE.HemisphereLight(0xffffff, 0xdedede, 0.4));
  const sun = new THREE.DirectionalLight(0xffffff, 1.5);
  sun.position.set(-0.5, 5, 1.1); sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, { left: -2.2, right: 2.2, top: 2.2, bottom: -2.2, near: 0.5, far: 10 });
  sun.shadow.bias = -0.0003; sun.shadow.normalBias = 0.015;
  scene.add(sun);

  const camera = new THREE.PerspectiveCamera(32, 1, 0.05, 60);
  const HOME = { pos: new THREE.Vector3(-1.9, 1.75, 3.6), target: new THREE.Vector3(0, 0.42, 0) };
  camera.position.copy(HOME.pos);
  const controls = new OrbitControls(camera, canvas);
  controls.target.copy(HOME.target);
  controls.enableDamping = true; controls.enablePan = false;
  controls.minDistance = 1.6; controls.maxDistance = 7;
  controls.maxPolarAngle = Math.PI * 0.5;

  const table = new THREE.Group(); scene.add(table);
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(12, 12), new THREE.ShadowMaterial({ opacity: 0.14 }));
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; table.add(ground);

  const texLoader = new THREE.TextureLoader(); texLoader.setCrossOrigin('anonymous');
  const matCache = {};
  const woodMat = key => {
    if (matCache[key]) return matCache[key];
    const t = texLoader.load(CFG.textures[key]);
    t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.MirroredRepeatWrapping;
    t.anisotropy = renderer.capabilities.getMaxAnisotropy();
    return (matCache[key] = new THREE.MeshPhysicalMaterial({ map: t, roughness: 0.42, clearcoat: 0.25, clearcoatRoughness: 0.5 }));
  };

  // Medidas em cm: tampo 3 cm (6 mm de aresta + assotado a 45°), sub-base 2 cm reta (+5 cm a toda a volta), pé oval
  const TOP_T = 3, EDGE = 0.6, SUB_T = 2, SUB_OVER = 5, SEG = 160;
  const cm = v => v / 100;
  const ellipseRing = (a, b, inset, y) => {
    const pts = [];
    for (let i = 0; i <= SEG; i++) {
      const t = (i / SEG) * Math.PI * 2, c = Math.cos(t), s = Math.sin(t);
      let nx = c / a, nz = s / b; const l = Math.hypot(nx, nz); nx /= l; nz /= l;
      pts.push(new THREE.Vector3(cm(a * c - nx * inset), cm(y), cm(b * s - nz * inset)));
    }
    return pts;
  };
  const band = (r1, r2) => {
    const pos = [], idx = [];
    r1.forEach((p, i) => pos.push(p.x, p.y, p.z, r2[i].x, r2[i].y, r2[i].z));
    for (let i = 0; i < SEG; i++) { const a = i * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(idx); g.computeVertexNormals(); return g;
  };
  const cap = (ring, up) => {
    const pos = [0, ring[0].y, 0], idx = [];
    ring.forEach(p => pos.push(p.x, p.y, p.z));
    for (let i = 1; i <= SEG; i++) up ? idx.push(0, i + 1, i) : idx.push(0, i, i + 1);
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(idx); g.computeVertexNormals(); return g;
  };
  function buildTop(s) {
    const a = s.L / 2, b = s.W / 2;
    const rTop = ellipseRing(a, b, 0, s.H), rEdge = ellipseRing(a, b, 0, s.H - EDGE), rBot = ellipseRing(a, b, TOP_T - EDGE, s.H - TOP_T);
    const g = mergeGeometries([cap(rTop, true), band(rTop, rEdge), band(rEdge, rBot), cap(rBot, false)]);
    const p = g.attributes.position, uv = [];
    for (let i = 0; i < p.count; i++) uv.push((p.getZ(i) + 0.6) / 1.0, (p.getX(i) + cm(s.L) / 2) / 1.1);   // veio ao comprimento
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    return g;
  }
  function oval(l, w, h, y0, uScale, vScale) {
    const g = new THREE.CylinderGeometry(0.5, 0.5, 1, 128, 1, false);
    g.scale(cm(l), cm(h), cm(w)); g.translate(0, cm(y0 + h / 2), 0);
    const uv = g.attributes.uv; for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * uScale, uv.getY(i) * vScale);
    g.computeVertexNormals(); return g;
  }
  const meshes = { top: new THREE.Mesh(), sub: new THREE.Mesh(), base: new THREE.Mesh() };
  Object.values(meshes).forEach(m => { m.castShadow = m.receiveShadow = true; table.add(m); });

  function buildScene() {
    const s = SIZES[state.size], mat = woodMat(MATS[state.mat].key), hBase = s.H - TOP_T - SUB_T;
    Object.values(meshes).forEach(m => m.geometry.dispose());
    meshes.top.geometry = buildTop(s);
    meshes.sub.geometry = oval(s.baseL + SUB_OVER * 2, s.baseW + SUB_OVER * 2, SUB_T, hBase, 2.6, 0.03);
    meshes.base.geometry = oval(s.baseL, s.baseW, hBase, 0, 2.4, 0.7);
    Object.values(meshes).forEach(m => m.material = mat);
  }
  function update() { buildScene(); renderUI(); }

  let anim = null;
  function resetView() { anim = { t: 0, p: camera.position.clone(), q: controls.target.clone() }; }
  function resize() {
    const w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h || renderer.xr.isPresenting) return;
    renderer.setSize(w, h, false); camera.aspect = w / h;
    camera.fov = w / h < 1 ? 44 : 32; camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(stage);

  /* ---------- Realidade aumentada ---------- */
  let hitSource = null, reticle = null;
  const toast = (m, ms = 3500) => { const t = q('toast'); t.textContent = m; t.hidden = false; clearTimeout(toast.t); toast.t = setTimeout(() => t.hidden = true, ms); };
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  async function startAR() {
    try {
      if (isIOS) {
        const { USDZExporter } = await import('three/addons/exporters/USDZExporter.js');
        ground.visible = false;
        const data = await new USDZExporter().parseAsync(table);
        ground.visible = true;
        const a = document.createElement('a'); a.rel = 'ar';
        a.href = URL.createObjectURL(new Blob([data], { type: 'model/vnd.usdz+zip' }));
        a.appendChild(document.createElement('img')); a.click();
        return;
      }
      if (navigator.xr && await navigator.xr.isSessionSupported('immersive-ar')) return startWebXR();
      toast(T.arDesktop);
    } catch (e) { ground.visible = true; toast(T.arFail); }
  }
  async function startWebXR() {
    const overlay = q('xr');
    const session = await navigator.xr.requestSession('immersive-ar', { requiredFeatures: ['hit-test'], optionalFeatures: ['dom-overlay'], domOverlay: { root: overlay } });
    overlay.hidden = false; q('xrmsg').textContent = T.arPlace; q('xrexit').textContent = T.exit;
    renderer.xr.setReferenceSpaceType('local');
    await renderer.xr.setSession(session);
    hitSource = await session.requestHitTestSource({ space: await session.requestReferenceSpace('viewer') });
    if (!reticle) {
      reticle = new THREE.Mesh(new THREE.RingGeometry(0.12, 0.14, 40).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xffffff }));
      reticle.matrixAutoUpdate = false; scene.add(reticle);
    }
    table.visible = false;
    session.addEventListener('select', () => { if (!reticle.visible) return; table.position.setFromMatrixPosition(reticle.matrix); table.visible = true; q('xrmsg').textContent = T.arMove; });
    q('xrexit').onclick = () => session.end();
    session.addEventListener('end', () => { hitSource = null; reticle.visible = false; overlay.hidden = true; table.position.set(0, 0, 0); table.visible = true; resize(); });
  }

  /* ---------- Ciclo ---------- */
  const clock = new THREE.Clock();
  const loop = (t, frame) => {
    const dt = clock.getDelta();
    if (frame && hitSource) {
      const res = frame.getHitTestResults(hitSource);
      if (res.length) { reticle.visible = true; reticle.matrix.fromArray(res[0].getPose(renderer.xr.getReferenceSpace()).transform.matrix); } else reticle.visible = false;
    } else {
      if (anim) { anim.t = Math.min(1, anim.t + dt / 0.6); const k = 1 - Math.pow(1 - anim.t, 3); camera.position.lerpVectors(anim.p, HOME.pos, k); controls.target.lerpVectors(anim.q, HOME.target, k); if (anim.t >= 1) anim = null; }
      controls.update();
    }
    renderer.render(scene, camera);
  };

  update();
  q('loading').hidden = true;
  return {
    setActive(on) { renderer.setAnimationLoop(on ? loop : null); if (on) { clock.getDelta(); resize(); } },
    // Abre com a mesma variante que o cliente tem escolhida na página
    syncFromPage() {
      const input = document.querySelector('.product-page-info form[action*="/cart/add"] [name="id"], form[action*="/cart/add"] [name="id"]');
      if (input && selectVariantId(input.value)) update();
    },
  };
}
